'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, ArrowUpRight, Check, ExternalLink, Lock, RefreshCw, X } from 'lucide-react';
import { fetchInvoice } from '@/lib/api';
import {
  onPaymentReturn,
  openPaymentWindow,
  popupPaymentsWanted,
  type PaymentWindow,
} from '@/lib/payment';
import { fmtMoney } from '@/lib/format';

/**
 * The page's half of paying in a popup, the way PayPal, Plaid and the OAuth
 * crowd do it:
 *
 *  - The window opens on the click (before any await) or the browser blocks
 *    it, so `begin()` is synchronous and `launch()` sends it to OLG later.
 *  - While it is open the page waits with the amount, the provider's name
 *    and one big "Show the payment window" button, because a popup slips
 *    behind the page the moment someone clicks anywhere else (PayPal's
 *    "Don't see the secure browser? Click to continue").
 *  - The window closing is a distinct state from finishing (Plaid's
 *    CLOSE_OAUTH vs onSuccess). Closing without a result reads the invoice
 *    once and, when nothing has landed, offers to open it again or pay later.
 *  - The popup's message is a hint (Stripe: the return only means the flow
 *    was exited). Settlement is always MP's rows, read here or by the card.
 *  - Small screens keep the in-place redirect; popups become tabs there.
 */

export type PayState =
  | { kind: 'idle' }
  | { kind: 'opening' }
  | { kind: 'open'; since: number }
  | { kind: 'closed' }
  | { kind: 'returned'; claimedSuccess: boolean }
  | { kind: 'error'; message: string };

const POLL_MS = 5000;
const WINDOW_CLOSED_POLL_MS = 600;
const OPEN_TOO_LONG_MS = 25 * 60 * 1000;

const SETTLED = new Set(['paid', 'partly-paid', 'confirming']);

export function usePaymentWindow({
  apiBaseUrl,
  onSettled,
}: {
  apiBaseUrl: string;
  /** MP says money landed (or OLG claimed it did): the caller refreshes its view. */
  onSettled: (invoiceGuid: string, claimedSuccess: boolean) => void;
}) {
  const [state, setState] = useState<PayState>({ kind: 'idle' });
  const [refocusHint, setRefocusHint] = useState(false);
  const winRef = useRef<PaymentWindow | null>(null);
  const guidRef = useRef<string | null>(null);
  const settledRef = useRef(false);

  const settle = useCallback(
    (claimed: boolean) => {
      if (settledRef.current) return;
      settledRef.current = true;
      winRef.current?.close();
      setState({ kind: 'returned', claimedSuccess: claimed });
      if (guidRef.current) onSettled(guidRef.current, claimed);
    },
    [onSettled]
  );

  const check = useCallback(async (): Promise<boolean> => {
    const guid = guidRef.current;
    if (!guid) return false;
    try {
      const res = await fetchInvoice(apiBaseUrl, guid, false);
      if (SETTLED.has(res.view.kind)) {
        settle(false);
        return true;
      }
    } catch {
      /* a blip; the next tick tries again */
    }
    return false;
  }, [apiBaseUrl, settle]);

  // The popup's own report, same origin only.
  useEffect(
    () =>
      onPaymentReturn((r) => {
        if (state.kind !== 'open' && state.kind !== 'opening') return;
        if (r.invoiceGuid && guidRef.current && r.invoiceGuid !== guidRef.current) return;
        settle(r.claimedSuccess);
      }),
    [state.kind, settle]
  );

  // While open: watch for the window closing, poll MP, and notice the person
  // coming back to this page without a result (the refocus hint).
  useEffect(() => {
    if (state.kind !== 'open') return;
    settledRef.current = false;
    let closedTimer = 0;
    const closedTick = window.setInterval(() => {
      const w = winRef.current;
      if (!w || !w.closed) return;
      window.clearInterval(closedTick);
      // Give the return message a beat to arrive before calling it closed.
      closedTimer = window.setTimeout(async () => {
        if (settledRef.current) return;
        const landed = await check();
        if (!landed && !settledRef.current) setState({ kind: 'closed' });
      }, 700);
    }, WINDOW_CLOSED_POLL_MS);
    const poll = window.setInterval(() => void check(), POLL_MS);
    const tooLong = window.setTimeout(() => {
      if (!settledRef.current) setState({ kind: 'closed' });
    }, OPEN_TOO_LONG_MS);
    const onFocus = () => {
      if (winRef.current && !winRef.current.closed) setRefocusHint(true);
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(closedTick);
      window.clearInterval(poll);
      window.clearTimeout(tooLong);
      window.clearTimeout(closedTimer);
      window.removeEventListener('focus', onFocus);
    };
  }, [state.kind, check]);

  /** Open the window now. Null means blocked (or a phone): navigate in place instead. */
  const begin = useCallback((): PaymentWindow | null => {
    if (!popupPaymentsWanted()) return null;
    const w = openPaymentWindow();
    if (!w) return null;
    winRef.current = w;
    settledRef.current = false;
    setRefocusHint(false);
    setState({ kind: 'opening' });
    return w;
  }, []);

  /** The API has minted the OLG URL: send the window there and start waiting. */
  const launch = useCallback((url: string, invoiceGuid: string) => {
    guidRef.current = invoiceGuid;
    const w = winRef.current;
    if (!w || w.closed) {
      // The window vanished before we had a URL: fall back to the page itself.
      window.location.assign(url);
      return;
    }
    w.setUrl(url);
    setState({ kind: 'open', since: Date.now() });
  }, []);

  /** Something failed before launch: close the placeholder and go quiet. */
  const abort = useCallback(() => {
    winRef.current?.close();
    winRef.current = null;
    setState({ kind: 'idle' });
  }, []);

  const focus = useCallback(() => {
    setRefocusHint(false);
    winRef.current?.focus();
  }, []);

  /** "Cancel": close the window, keep the seats held; the caller shows pay-later. */
  const cancel = useCallback(() => {
    winRef.current?.close();
    winRef.current = null;
    setState({ kind: 'closed' });
  }, []);

  const reset = useCallback(() => setState({ kind: 'idle' }), []);

  return { state, refocusHint, begin, launch, abort, focus, cancel, check, reset };
}

export type PaymentWindowControls = ReturnType<typeof usePaymentWindow>;

/**
 * What the page shows while the money happens elsewhere. One card, four
 * moods: opening, open (with the refocus nudge), closed without a result,
 * and settled.
 */
export function PaymentPanel({
  pay,
  amount,
  onPayHere,
  onOpenAgain,
  payLaterHref,
  compact = false,
}: {
  pay: PaymentWindowControls;
  amount: number | null;
  /** Leave the page for OLG instead (the fallback for a lost window). */
  onPayHere: () => void;
  /** Mint a fresh URL and open a new window (the token may have expired). */
  onOpenAgain: () => void;
  payLaterHref?: string;
  compact?: boolean;
}) {
  const { state, refocusHint } = pay;
  const pad = compact ? 'p-4' : 'p-5 sm:p-6';
  const amountText = amount != null ? fmtMoney(amount) : null;

  if (state.kind === 'idle') return null;

  if (state.kind === 'returned') {
    return (
      <div className={`card ${pad} rise`}>
        <div className="flex items-start gap-3">
          <span className="bg-teal flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white">
            <Check className="h-5 w-5" strokeWidth={3} />
          </span>
          <div className="min-w-0">
            <p className="text-navy font-extrabold">
              {state.claimedSuccess ? 'Payment received' : 'Back from the payment window'}
            </p>
            <p className="text-slate mt-0.5 text-sm">
              {state.claimedSuccess
                ? 'Checking it against our records now. A receipt is on its way from Online Giving.'
                : 'Checking where things stand.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state.kind === 'closed') {
    return (
      <div className={`card ${pad} rise`}>
        <div className="flex items-start gap-3">
          <span className="bg-amber-soft text-amber flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
            <AlertCircle className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-navy font-extrabold">
              The payment window closed before we heard back
            </p>
            <p className="text-slate mt-0.5 text-sm">
              If you finished paying, give it a moment and check again. Nothing is lost either way:
              your seats stay held while you decide.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-primary !py-2.5 text-sm"
                onClick={onOpenAgain}
              >
                <ExternalLink className="h-4 w-4" /> Open payment again
              </button>
              <button
                type="button"
                className="btn btn-quiet !py-2.5 text-sm"
                onClick={() => void pay.check()}
              >
                <RefreshCw className="h-4 w-4" /> Check again
              </button>
              <button type="button" className="btn btn-quiet !py-2.5 text-sm" onClick={onPayHere}>
                Pay on this page
              </button>
            </div>
            {payLaterHref && (
              <a
                href={payLaterHref}
                className="text-teal-deep mt-3 inline-block text-xs font-bold hover:underline"
              >
                Pay later, or send the link to whoever is paying
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className={`card ${pad}`}>
        <p className="text-rose text-sm font-semibold">{state.message}</p>
        <button type="button" className="btn btn-quiet mt-3 !py-2.5 text-sm" onClick={onPayHere}>
          Pay on this page
        </button>
      </div>
    );
  }

  // opening | open
  const open = state.kind === 'open';
  return (
    <div className={`card overflow-hidden ${compact ? '' : ''}`}>
      <div
        className="relative overflow-hidden px-5 py-4 text-white sm:px-6"
        style={{ background: 'linear-gradient(135deg, #1c2b39 0%, #243848 50%, #2d4a5c 100%)' }}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute -top-20 -right-12 h-48 w-48 rounded-full opacity-60 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, var(--color-teal), transparent)' }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10">
            <Lock className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold tracking-widest text-white/60 uppercase">
              Secure payment
            </p>
            <p className={`text-lg leading-tight font-extrabold ${compact ? '' : 'truncate'}`}>
              {amountText ? `Pay ${amountText}` : 'Paying'} at onlinegiving.org
            </p>
          </div>
          <span
            className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-white/30 border-t-white"
            aria-hidden
          />
        </div>
      </div>
      <div className={`${pad} space-y-3`}>
        <p className="text-slate-deep text-sm">
          {open
            ? 'We opened our payment provider in a separate window. Finish there and this page updates on its own.'
            : 'Opening our payment provider in a separate window…'}
        </p>
        {refocusHint && (
          <p className="bg-amber-soft text-amber rounded-xl px-3 py-2 text-xs font-semibold">
            Don&apos;t see the payment window? It is behind this one.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={`btn btn-primary !py-2.5 text-sm ${refocusHint ? 'ring-amber ring-2 ring-offset-2' : ''}`}
            onClick={pay.focus}
            disabled={!open}
          >
            <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} /> Show the payment window
          </button>
          <button type="button" className="btn btn-quiet !py-2.5 text-sm" onClick={onPayHere}>
            Pay here instead
          </button>
          <button
            type="button"
            className="btn btn-quiet !py-2.5 text-sm"
            onClick={pay.cancel}
            aria-label="Cancel"
          >
            <X className="h-4 w-4" /> Cancel
          </button>
        </div>
        <p className="text-slate text-xs">
          You are signed in to Online Giving there if you were before. Your seats are held for about
          three hours.
        </p>
      </div>
    </div>
  );
}
